import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import SystemNotification, { SystemNotificationApplication, SystemNotificationButtonAction, SystemNotificationDisplayType, SystemNotificationResponse } from "./Model/SystemNotification";
import { abpnClient } from "../Services/AbpnClient";
import { AuthContext } from "../Auth/AuthProvider";
import SystemNotificationModal from "./SystemNotificationModal";
import SystemNotificationBanner from "./SystemNotificationBanner";
import { deepCopy } from "../Shared/Utility/DeepCopy";
import isNullOrUndefined from "../Shared/Utility/isNullOrUndefined";
import { canNavigateToUrl, isAbsoluteUrl } from "../Shared/Utility/urlUtil";

interface ModalVisibilityState {
    visibilityState : {[id: string]: boolean};
    visibleIndex: number;
    modalCount: number;
}

export default function NotificationManager() {
    const authContext = useContext(AuthContext);
    const navigate = useNavigate();

    const [notifications, setNotifications] = useState<SystemNotification[]>([]);
    const [bannerNotifications, setBannerNotifications] = useState<SystemNotification[]>([]);
    const [modalNotifications, setModalNotifications] = useState<SystemNotification[]>([]);
    const [modalVisbilityState, setModalVisbilityState] = useState<ModalVisibilityState>({ visibilityState: {}, visibleIndex: 0, modalCount: 0});

    useEffect(() => {
        async function getSystemNotifications() {
            const _notifications = await abpnClient.get<SystemNotification[]>("/api/systemNotifications", authContext);
            setNotifications(_notifications);
        };

        getSystemNotifications();
    }, []);
    
    useEffect(() => {
        setBannerNotifications(notifications.filter(n => n.displayType == SystemNotificationDisplayType.Banner))
        setModalNotifications(notifications.filter(n => n.displayType == SystemNotificationDisplayType.Modal));
    }, [notifications]);


    useEffect(() => {
        if (modalVisbilityState.modalCount !== modalNotifications.length) {
            let newModalVisibilityState : ModalVisibilityState = {
                modalCount: modalNotifications.length,
                visibleIndex: 0,
                visibilityState: {}
            };

            if (modalNotifications.length > 0) newModalVisibilityState.visibilityState[modalNotifications[newModalVisibilityState.visibleIndex].systemNotificationId] = true;

            setModalVisbilityState(newModalVisibilityState)
        }

    }, [modalNotifications.length]);

    const navigateToPrimaryActionUrl = (primaryActionUrl : string) => {
        if (isNullOrUndefined(primaryActionUrl) || primaryActionUrl.trim().length == 0) return;

        if (isAbsoluteUrl(primaryActionUrl))
        {
            if (canNavigateToUrl(primaryActionUrl))
            {
                window.location.replace(primaryActionUrl);
            }
        }
        else 
        {
            navigate(new URL(primaryActionUrl, document.location.origin).pathname);
        }
    };

    const onNotificationAction = async (notificationActionType : SystemNotificationButtonAction, notification: SystemNotification) => {
        //Remove the banner notification
        if (notification.displayType == SystemNotificationDisplayType.Banner){
            const notificationsCopy = deepCopy(notifications);
            notificationsCopy.splice(notifications.findIndex(n => n.systemNotificationId==notification.systemNotificationId),1);
            setNotifications(notificationsCopy);
        }

        if (notification.displayType == SystemNotificationDisplayType.Modal) {
            const currentVisibleModalIndex = modalVisbilityState.visibleIndex;
            let nextModalVisbilityState = {...modalVisbilityState};
            nextModalVisbilityState.visibilityState[modalNotifications[currentVisibleModalIndex].systemNotificationId] = false;

            if (modalVisbilityState.visibleIndex < modalNotifications.length - 1) {
                const nextVisibleModalIndex = 1 + currentVisibleModalIndex;
                nextModalVisbilityState.visibleIndex = nextVisibleModalIndex;

                nextModalVisbilityState.visibilityState[modalNotifications[nextVisibleModalIndex].systemNotificationId] = true;
            }

            setModalVisbilityState(nextModalVisbilityState);
        }

        switch (notificationActionType){
            case SystemNotificationButtonAction.PrimaryAction: {
                await abpnClient.post<Partial<SystemNotificationResponse>>("/api/systemNotifications/saveSystemNotificationResponse", { systemNotificationApplicationId: SystemNotificationApplication.PreCert, primaryActionTaken: true, permanentlyDismiss: false, systemNotificationId: notification.systemNotificationId}, authContext);
                if (notification.primaryActionUrl) navigateToPrimaryActionUrl(notification.primaryActionUrl);
                break;
            }
            case SystemNotificationButtonAction.DontRemindMeAction: {
                await abpnClient.post<Partial<SystemNotificationResponse>>("/api/systemNotifications/saveSystemNotificationResponse", { systemNotificationApplicationId: SystemNotificationApplication.PreCert, primaryActionTaken: false, permanentlyDismiss: true, systemNotificationId: notification.systemNotificationId}, authContext);
                break;
            }
            default:
            case SystemNotificationButtonAction.CloseAction: {
                break;
            }
        }

    };

    const modalNotificationElements = modalNotifications.map(notification => <SystemNotificationModal key={notification.systemNotificationId}
        showModal={notification.systemNotificationId in modalVisbilityState.visibilityState && modalVisbilityState.visibilityState[notification.systemNotificationId] == true}
        onNotificationAction={onNotificationAction}
        notification={notification}/>);

    const bannerNotificationElements = bannerNotifications.map(notification => <SystemNotificationBanner key={notification.systemNotificationId}
        onNotificationAction={onNotificationAction}
        notification={notification}/>);

    return (<div className="system-notifications">
        {modalNotificationElements}
        {bannerNotificationElements}       
    </div>);
}